import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaPause, FaPlay } from "react-icons/fa";
import { Check } from "lucide-react";
import CustomToast from "../../misc/CustomToast";
import { getAuthHeader } from "../../../utils/authHeader";
import { cn } from "../../../utils/classMerger";
import { MusicType } from "../../listings/slideshow/types";

interface AutoVideoMusicSelectorProps {
    /** Selected track filenames — the pool the auto-video rotation picks from. */
    selected: string[];
    onChange: (tracks: string[]) => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const AutoVideoMusicSelector: React.FC<AutoVideoMusicSelectorProps> = ({
    selected,
    onChange,
}) => {
    const [tracks, setTracks] = useState<MusicType[]>([]);
    const [playingTrack, setPlayingTrack] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        const fetchMusic = async (): Promise<void> => {
            try {
                const headers = await getAuthHeader();
                const { data } = await axios.get<MusicType[]>(
                    `${backendUrl}/listing/slideshow/music`,
                    { headers }
                );
                setTracks(Array.isArray(data) ? data : []);
            } catch {
                // Non-fatal — the picker just renders empty.
            }
        };
        fetchMusic();
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const togglePlay = (url: string): void => {
        if (!audioRef.current) {
            audioRef.current = new Audio();
            audioRef.current.addEventListener("ended", () =>
                setPlayingTrack(null)
            );
        }
        if (playingTrack === url) {
            audioRef.current.pause();
            setPlayingTrack(null);
            return;
        }
        audioRef.current.src = url;
        audioRef.current.play().catch(() => {
            toast.error(
                <CustomToast message="Failed to play the track." type="error" />,
                { autoClose: 3000 }
            );
        });
        setPlayingTrack(url);
    };

    const toggleSelect = (name: string): void => {
        onChange(
            selected.includes(name)
                ? selected.filter((t) => t !== name)
                : [...selected, name]
        );
    };

    return (
        <div className="space-y-3">
            <div>
                <h4 className="text-base font-semibold text-primary">Music</h4>
                <p className="text-sm text-neutral-500">
                    Auto-generated videos rotate through the tracks you select.
                    Leave all unselected for no background music.
                </p>
            </div>
            <div className="grid grid-cols-1 min-[640px]:grid-cols-2 gap-2">
                {tracks.map((track) => {
                    const isSelected = selected.includes(track.name);
                    return (
                        <div
                            key={track.name}
                            onClick={() => toggleSelect(track.name)}
                            className={cn(
                                "flex items-center justify-between gap-3 rounded-md border p-2 cursor-pointer transition-colors",
                                isSelected
                                    ? "border-secondary bg-lightSecondary"
                                    : "border-gray-200 hover:border-gray-300"
                            )}
                        >
                            <div className="flex min-w-0 items-center gap-2">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        togglePlay(track.url);
                                    }}
                                    aria-label={
                                        playingTrack === track.url
                                            ? "Pause"
                                            : "Play"
                                    }
                                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full hover:bg-gray-100"
                                >
                                    {playingTrack === track.url ? (
                                        <FaPause className="h-3 w-3 text-secondary" />
                                    ) : (
                                        <FaPlay className="h-3 w-3 text-secondary" />
                                    )}
                                </button>
                                <span className="truncate text-sm text-primary">
                                    {track.name.replace(/\.mp3$/i, "")}
                                </span>
                            </div>
                            <span
                                className={cn(
                                    "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border",
                                    isSelected
                                        ? "border-secondary bg-secondary text-primary"
                                        : "border-gray-300"
                                )}
                            >
                                {isSelected && <Check className="h-3 w-3" />}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AutoVideoMusicSelector;
